import type { MailboxInfo, MailboxKey } from 'owa-client-types';
import { getMailboxKeyForMailboxInfo } from 'owa-client-types';
import type CategoryType from 'owa-service/lib/contract/CategoryType';
import { getAccountScopeUserSettings, type ReadOnlyAccountConfiguration } from 'owa-session-store';
import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { isCapabilityEnabled } from 'owa-capabilities';
import { categoryListCapability } from 'owa-capabilities-definitions/lib/categoryListCapability';
import getMailboxRequestOptions from 'owa-service/lib/getMailboxRequestOptions';
import getMasterCategoryListOperation from 'owa-service/lib/operation/getMasterCategoryListOperation';
import { isSharedMailboxInfo } from 'owa-client-types/lib/SharedMailboxInfo';
import { isMailboxSharedResourceArchive } from 'owa-account-shared-mailbox-utils';
import { getMailboxInfoForShared } from 'owa-mailbox-info/lib/getMailboxInfoForShared';

// A map to cache the master category list for shared mailboxes
// to avoid making multiple requests for the same mailbox.
const sharedMailboxMasterCategoryMap = new Map<MailboxKey, CategoryType[]>();

/**
 * Gets the MasterCategoryList for the given mailboxInfo.
 * @param mailboxInfo The mailbox to fetch the user configuration for
 */
export default function getMasterCategoryList(mailboxInfo: MailboxInfo): CategoryType[] {
    let userConfiguration: ReadOnlyAccountConfiguration;

    if (!isCapabilityEnabled(categoryListCapability, mailboxInfo)) {
        // if the account doesn't support a category list of its own, fall back to using the
        // category list of the global settings account
        userConfiguration = getAccountScopeUserSettings(getGlobalSettingsAccountMailboxInfo());
    } else {
        // For shared mailboxes, we should use the cached category list if available
        const isSharedResourceArchive = isMailboxSharedResourceArchive(mailboxInfo);
        if (isSharedMailboxInfo(mailboxInfo) || isSharedResourceArchive) {
            let mailboxInfoToUse = mailboxInfo;
            // For shared resource archives, we need to get the mailbox info for the shared mailbox as those are the categories we want to show
            if (isSharedResourceArchive && mailboxInfo.explicitLogonMailboxSmtpAddress) {
                mailboxInfoToUse = getMailboxInfoForShared(
                    mailboxInfo,
                    mailboxInfo.explicitLogonMailboxSmtpAddress
                );
            }
            const mailboxKey = getMailboxKeyForMailboxInfo(mailboxInfoToUse);
            // If not found, return an empty array so we don't show the wrong category data for a different mailbox
            // based on the getAccountScopeUserSettings of the main account mailbox info
            return sharedMailboxMasterCategoryMap.get(mailboxKey) || [];
        }

        // if the account supports a category list, use the mailboxInfo to get the user configuration
        userConfiguration = getAccountScopeUserSettings(mailboxInfo);
    }

    const masterCategoryList = userConfiguration?.MasterCategoryList;
    return <CategoryType[]>masterCategoryList?.MasterList || [];
}

/**
 * Initializes the master category list for shared mailboxes.
 * @param mailboxInfo The shared mailboxinfo to fetch the master category list for
 */
export async function initializeMasterCategoryListForSharedMailbox(
    mailboxInfo: MailboxInfo
): Promise<void> {
    // If the mailboxInfo is not a shared mailbox or if already initialized, return
    const mailboxKey = getMailboxKeyForMailboxInfo(mailboxInfo);
    if (!isSharedMailboxInfo(mailboxInfo) || sharedMailboxMasterCategoryMap.has(mailboxKey)) {
        return;
    }

    const response = await getMasterCategoryListOperation(
        { request: {} },
        getMailboxRequestOptions(mailboxInfo)
    );

    // We will set the map even if it fails due to perms so users don't see wrong category
    // data on the dropdown
    sharedMailboxMasterCategoryMap.set(mailboxKey, response?.MasterList ?? []);
}
